import {
  Alert,
  Anchor,
  Box,
  Card,
  Container,
  createStyles,
  Divider,
  Group,
  LoadingOverlay,
  Stack,
  Text,
  Title,
  useMantineTheme
} from '@mantine/core'
import { useForceUpdate, useMediaQuery } from '@mantine/hooks'
import { IconAlertCircle, IconSchool } from '@tabler/icons'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'
import React, { useEffect } from 'react'

import { Redirect } from '@/components/commons'
import { useFetch } from '@/lib/hooks'
import { getSession } from '@/lib/utils'
import { useAuth } from '@/providers/AuthProvider'
import {
  Channel as ChannelType,
  Segment as SegmentType,
  SegmentGrade as SegmentGradeType,
  Site as SiteType
} from '@/types'

import { Segment } from './components'

interface Data {
  site?: SiteType
  channel?: ChannelType
}

interface Props {
  initialData?: Data
  children?: React.ReactNode
}

const useStyles = createStyles(theme => ({
  hero: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing.xl * 2,
    [theme.fn.smallerThan('lg')]: { padding: theme.spacing.lg }
  },
  heroContent: {
    marginRight: theme.spacing.xl * 2,
    [theme.fn.smallerThan('md')]: { marginRight: 0 }
  },
  heroImageWrapper: {
    position: 'relative',
    width: 250,
    height: 250,
    marginLeft: 'auto',
    [theme.fn.smallerThan('md')]: { display: 'none' }
  }
}))

export default function ChannelView({ initialData }: Props) {
  // Hooks
  const router = useRouter()
  const forceUpdate = useForceUpdate()
  const { isAuthenticated } = useAuth() || {}
  const { site: siteSlug, channel: channelSlug } = router.query || {}
  const { classes } = useStyles()
  const theme = useMantineTheme()
  const isXs = useMediaQuery(`(max-width: ${theme.breakpoints.xs}px)`)
  const isMd = useMediaQuery(`(min-width: ${theme.breakpoints.md}px)`)

  // Fetch
  const { data: siteData } = useFetch(
    [siteSlug && channelSlug ? `/${siteSlug}/` : null],
    { fallbackData: initialData?.site } // rehydration if necessary
  )

  // Constants
  const sessionStudentUid = getSession('stuid')
  const isSitePrivate = siteData?.privacy === 'private'

  // Fetch
  const { data: channelData } = useFetch(
    [siteSlug && channelSlug ? `/${siteSlug}/channels/${channelSlug}/` : null],
    { fallbackData: initialData?.channel } // rehydration if necessary
  )

  const { data: gradesData, error: gradesDataError } = useFetch([
    !!router?.isReady && siteSlug && channelSlug && siteData
      ? `/${siteSlug}/channels/${channelSlug}/grades/`
      : null,
    isSitePrivate ? { student_site_profile: sessionStudentUid || '' } : {}
  ])

  const isLoading = !gradesData && !gradesDataError
  const segments = gradesData?.reduce(
    (acc: SegmentGradeType[], grade: SegmentGradeType) => [...acc, ...grade?.segments],
    []
  )
  const stores = segments?.reduce(
    (acc: SegmentType[], segment: SegmentType) => [...acc, ...segment?.stores],
    []
  )

  // Effects
  useEffect(() => {
    forceUpdate()
  }, [isAuthenticated, forceUpdate])

  // Return
  if (channelSlug !== channelData?.slug) {
    return <Redirect url={`/${channelData?.slug}`} />
  }

  if (gradesDataError || !gradesData?.length) {
    return (
      <Container size="xl" py="xl">
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Erro de configuração"
          color="orange"
          mb="md"
          hidden={isLoading}>
          <Text>
            Houve um erro de configuração das lojas. Entre em contato na{' '}
            <Anchor size="sm" href="/help" component={Link}>
              Central de Ajuda
            </Anchor>
            .
          </Text>
        </Alert>
      </Container>
    )
  }

  if (stores.length === 1) return <Redirect url={`/${channelSlug}/${stores[0].slug}`} replace />

  return (
    <Container size="xl" py="xl">
      <LoadingOverlay visible={isLoading} />
      {channelData && channelData?.displayBanner && (
        <Card withBorder mb="xl" hidden={isXs}>
          <Card.Section className={classes.hero}>
            <Box className={classes.heroContent}>
              <Title order={isMd ? 1 : 4}>{channelData.title}</Title>
              <Text
                color="dimmed"
                my={isMd ? 'xl' : 'xs'}
                size={isMd ? 'lg' : 'sm'}
                dangerouslySetInnerHTML={{
                  __html: channelData.description
                    ? channelData.description.replace(/\n/g, '<br />')
                    : `Seja bem-vindo(a) à loja oficial - ${channelData.title}!`
                }}
              />
              <Text size={isMd ? 'md' : 'sm'} weight={700}>
                {initialData?.site?.beneficiary === 'shopper'
                  ? 'Selecione abaixo a vitrine de compras para comprar produtos e serviços.'
                  : 'Selecione a vitrine de compras de acordo com a turma do aluno que deseja comprar produtos e serviços.'}
              </Text>
            </Box>
            <Box className={classes.heroImageWrapper}>
              <Image
                src={channelData.logo || '/images/placeholders/store-hero.png'}
                alt={channelData.title}
                fill
                quality={60}
                style={{ objectFit: 'contain' }}
              />
            </Box>
          </Card.Section>
        </Card>
      )}
      <Stack>
        {Array.isArray(gradesData) &&
          !!gradesData.length &&
          gradesData.map(grade => (
            <Box key={grade?.uid}>
              {gradesData.length > 1 && (
                <Divider
                  size="sm"
                  variant="dotted"
                  mb="xs"
                  label={
                    <Group spacing="xs">
                      <IconSchool size={!isXs ? 24 : 18} />
                      <Title order={!isXs ? 3 : 5}>{grade?.displayName}</Title>
                    </Group>
                  }
                />
              )}
              {Array.isArray(grade?.segments) && grade.segments.length ? (
                grade.segments.map((segment: SegmentType) => (
                  <Segment data={segment} channel={channelData} key={segment.uid} />
                ))
              ) : (
                <Alert icon={<IconAlertCircle size={16} />} color="orange" mb="md">
                  <Text>Oops, houve um erro. Não encontramos nenhuma loja deste setor.</Text>
                </Alert>
              )}
            </Box>
          ))}
      </Stack>
    </Container>
  )
}
